import type { AttributChoix } from '@/models/interfaces/AttributChoix'
import { apiClient } from '@/plugins/axios/api'

const ATTRIBUT_CHOIX_STATUT_ACTIF = 'ACTIF'

/**
 * Admin list item: choice with optional parent attribute info.
 * From GET /api/attribut_choixes (paginated).
 */
export interface AttributChoixAdminItem {
  agcId: number
  fkAtgId: number
  agcRefCode: string
  agcValeur: string
  /** Valeur anglaise (agc_valeur_en), absente tant qu'elle n'est pas saisie. */
  agcValeurEn?: string | null
  agcOrdre?: number
  agcStatut: string
  atgId?: number
  atgRefCode?: string
  atgTitre?: string
}

/**
 * GET /api/admin/attribut_choixes
 * List all attribute choices for admin. Plain array (no Hydra, no pagination).
 * Response: agcId, fkAtgId, agcRefCode, agcValeur, agcOrdre, agcStatut, atgId, atgRefCode, atgTitre.
 */
export async function getAllAdmin(): Promise<AttributChoixAdminItem[]> {
  const response = await apiClient.get<Record<string, unknown>[] | unknown>('/api/admin/attribut_choixes')
  const raw = Array.isArray(response.data) ? response.data : []
  return raw.map((row) => normalizeAttributChoixAdminItem(row as Record<string, unknown>))
}

function normalizeAttributChoixAdminItem(row: Record<string, unknown>): AttributChoixAdminItem {
  return {
    agcId: (row.agcId ?? row.agc_id) as number,
    fkAtgId: (row.fkAtgId ?? row.fk_atg_id) as number,
    agcRefCode: String(row.agcRefCode ?? row.agc_ref_code ?? ''),
    agcValeur: String(row.agcValeur ?? row.agc_valeur ?? ''),
    agcOrdre: (row.agcOrdre ?? row.agc_ordre) as number | undefined,
    agcStatut: String(row.agcStatut ?? row.agc_statut ?? ''),
    atgId: (row.atgId ?? row.atg_id) as number | undefined,
    atgRefCode: (row.atgRefCode ?? row.atg_ref_code) as string | undefined,
    atgTitre: (row.atgTitre ?? row.atg_titre) as string | undefined,
    agcValeurEn: (row.agcValeurEn ?? row.agc_valeur_en) as string | null | undefined,
  }
}

/**
 * GET /api/attribut_choixes?attribut=/api/attributs/{id}
 * Returns choices for a single attribute (API Platform collection). Filtered to ACTIF only.
 */
export function getChoicesByAttributId(atgId: number): Promise<AttributChoix[]> {
  const attributIri = `/api/attributs/${encodeURIComponent(String(atgId))}`
  return apiClient
    .get('/api/attribut_choixes', { params: { attribut: attributIri } })
    .then((response) => {
      const raw = response.data['hydra:member'] ?? response.data ?? []
      const items = Array.isArray(raw) ? raw : []
      return items
        .map(normalizeAttributChoix)
        .filter((c: AttributChoix) => (c.agcStatut ?? c.agc_statut) === ATTRIBUT_CHOIX_STATUT_ACTIF)
    })
    .catch((error) => {
      console.warn(`[AttributChoixService] Failed to load choices for atgId=${atgId}:`, error)
      return []
    })
}

function normalizeAttributChoix(row: Record<string, unknown>): AttributChoix {
  return {
    agcId: (row.agcId ?? row.agc_id) as number,
    fkAtgId: (row.fkAtgId ?? row.fk_atg_id) as number,
    agcRefCode: (row.agcRefCode ?? row.agc_ref_code ?? '') as string,
    agcValeur: (row.agcValeur ?? row.agc_valeur ?? '') as string,
    agcOrdre: (row.agcOrdre ?? row.agc_ordre) as number | undefined,
    agcStatut: (row.agcStatut ?? row.agc_statut ?? '') as string,
  }
}

/**
 * Payload for create/update. Send parent via `attribut` (number or IRI), not fkAtgId.
 */
export interface CreateAttributChoixPayload {
  /** Parent attribut: ID (e.g. 5) or IRI (e.g. "/api/attributs/5"). */
  attribut: number | string
  agcRefCode: string
  agcValeur: string
  /** Valeur anglaise, facultative : l'option peut n'être saisie qu'en français. */
  agcValeurEn?: string | null
  agcOrdre?: number
  agcStatut?: string
}

/**
 * POST /api/attribut_choixes
 * Body: attribut (number or IRI), agcRefCode, agcValeur, agcOrdre. Do not send fkAtgId.
 */
export function createChoix(payload: CreateAttributChoixPayload): Promise<AttributChoix> {
  return apiClient
    .post<AttributChoix>('/api/attribut_choixes', payload, {
      headers: { 'Content-Type': 'application/json' },
    })
    .then((res) => res.data)
}

/**
 * PUT /api/attribut_choixes/{id}
 * Body: same shape as create (attribut, agcRefCode, agcValeur, agcOrdre). Do not send fkAtgId.
 */
export function updateChoix(
  id: number,
  payload: Partial<CreateAttributChoixPayload>,
): Promise<AttributChoix> {
  return apiClient
    .put<AttributChoix>(`/api/attribut_choixes/${encodeURIComponent(String(id))}`, payload, {
      headers: { 'Content-Type': 'application/json' },
    })
    .then((res) => res.data)
}

/**
 * DELETE /api/attribut_choixes/{id}
 */
export function deleteChoix(id: number): Promise<void> {
  return apiClient
    .delete(`/api/attribut_choixes/${encodeURIComponent(String(id))}`)
    .then(() => undefined)
}
